import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, InputAdornment } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import {
  IconUpload,
  IconSettings,
  IconDownload,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";

import { paths } from "../../../routes/paths";
import {
  addCandidate,
  removeCandidate,
  clearCandidates,
  showAppAlert,
} from "../../../store/reducers/appreducer";
import {
  selectCandidatesForInterviewAPI,
  updateCandidateEmailAPI,
  updateCandidatePhoneAPI,
  retryResumeAPI,
} from "../../../services/resumeService";
import {
  scheduleScreeningCallAPI,
  getScreeningStatusesAPI,
  getScreeningStatusAPI,
  pauseScreeningCallAPI,
  getBestScreeningResult,
  getScreeningData,
} from "../../../services/screeningService";
import {
  startBatchCallAPI,
  getBatchStatusAPI,
  fetchInterviewData,
} from "../../../services/interviewService";
import { setDecisionAPI } from "../../../services/decisionService";
import { fetchJobByIdAPI } from "../../../services/jobService";
import { exportToCsv } from "../../../utils/exportCsv";

import { INTERVIEWS_ENABLED } from "../../config/features";
import { useConsole, jobIdOf } from "./ConsoleContext";
import { toRow, sortRows, filterByStage, countByStage, DECISION } from "./candidateRow";
import { hasWorkInFlight } from "./resumeStatus";
import { isPending } from "./screeningStatus";
import { shortlistGuards, shortlistReason } from "./decisionGuard";
import { pauseGuards, pauseReason } from "./pauseGuard";
import StageTabs, { STAGE_TABS } from "./StageTabs";
import CandidateTable from "./CandidateTable";
import SelectionBar from "./SelectionBar";
import BatchCallDialog from "./BatchCallDialog";
import UploadModal from "./UploadModal";
import JobPanel from "./JobPanel";
import CandidateDetailPanel from "./CandidateDetailPanel";
import InterviewPanel from "./InterviewPanel";
import PeerCompareDialog from "./PeerCompareDialog";
import ColumnPicker from "./ColumnPicker";
import { availableFields, formatValue } from "./screeningFields";
import useScreeningColumns from "../../hooks/useScreeningColumns";
import { META_FIELDS, isMetaKey } from "./metaColumns";
import useMetaColumns from "../../hooks/useMetaColumns";
import { score2 } from "./candidateExport";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

const BATCH_POLL_MS = 10000;
const SCREENING_POLL_MS = 30000;
// Faster than the others because this one is watched: the user has just dropped files and is
// staring at the rows waiting for them to land. It stops the moment nothing is in flight.
const PIPELINE_POLL_MS = 3000;

/** What we call each decision to the user, and which tab it lands them on. */
const DECISION_LABEL = {
  [DECISION.SHORTLISTED]: "the shortlist",
  [DECISION.BACKLOG]: "the backlog",
  [DECISION.REJECTED]: "rejected",
  [DECISION.UNDECIDED]: "review",
};

const DECISION_TAB = {
  [DECISION.SHORTLISTED]: "shortlist",
  [DECISION.BACKLOG]: "backlog",
  [DECISION.REJECTED]: "rejected",
};

/** Maps lead_status to the SCREEN column text. Mirrors the old Screening dashboard. */
const SCREEN_LABEL = {
  in_progress: "Pending",
  qualified: "Qualified",
  "intent-qualified": "Qualified",
  disqualified: "Disqualified",
  "completed-RNR": "No answer",
  dnd: "Do not call",
  paused: "Paused",
};


/**
 * Search: name, email, or phone.
 *
 * The phone side compares digits only, so "98 765" finds "+91 98765-43210" — a recruiter reading a
 * number off a CV types the spacing they see, which is never the spacing we stored. Guarded on
 * `digits.length` so a plain text query like "rao" doesn't degenerate into an empty-string
 * `includes("")` and match every phone number in the job.
 */
const matchesQuery = (row, q) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const digits = needle.replace(/\D/g, "");
  return (
    String(row.name || "").toLowerCase().includes(needle) ||
    String(row.email || "").toLowerCase().includes(needle) ||
    (digits.length > 0 && String(row.phone || "").replace(/\D/g, "").includes(digits))
  );
};

/** "2026-07-02" -> "Jul 2". Falls back to the raw value rather than showing "Invalid Date". */
const formatPostingDate = (value) => {
  const d = DateTime.fromISO(String(value));
  return d.isValid ? d.toFormat("LLL d") : String(value);
};

const Console = () => {
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    jobs,
    loadRankings,
    rankingsByJob,
    refreshCounts,
    reloadJobs,
    setCommands,
  } = useConsole();
  const selectedCandidates = useSelector((s) => s.appreducer.selectedCandidates);

  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "total", dir: "desc" });
  const [screeningByResumeId, setScreeningByResumeId] = useState({});
  const [interviewByResumeId, setInterviewByResumeId] = useState({});
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [peerOpen, setPeerOpen] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Opening a job lands on Resume Review — the first tab, and the top of the funnel: the résumés
  // nobody has called and nobody has ruled on. It can be empty on a fully-triaged job, which is
  // fine: an empty review queue is the goal, and the table's empty state offers Upload résumés,
  // which is exactly what you'd do next.
  //
  // A bookmark or an old link can still name a tab that no longer exists (?stage=interview while
  // the flag is off, or ?stage=applied from before the partition). Fall back rather than render a
  // tab strip with nothing selected.
  const requestedStage = searchParams.get("stage") || "review";
  const stage = STAGE_TABS.some((t) => t.key === requestedStage) ? requestedStage : "review";
  const rankings = useMemo(() => rankingsByJob[jobId] || [], [rankingsByJob, jobId]);

  // The rail only lists the signed-in user's OWN jobs, so a super_admin opening another
  // company's job from the All Jobs page (or any deep link) won't find it here. The candidate
  // table loads independently via loadRankings; this fallback fetch is only so the header
  // (title / code / location / date) populates for a job that isn't in the rail.
  const jobFromRail = jobs.find((j) => String(jobIdOf(j)) === String(jobId));
  const inRail = Boolean(jobFromRail);
  const [fetchedJob, setFetchedJob] = useState(null);
  const job = jobFromRail || fetchedJob;

  useEffect(() => {
    if (inRail || !jobId) {
      setFetchedJob(null);
      return;
    }
    let cancelled = false;
    fetchJobByIdAPI(jobId).then((data) => {
      if (cancelled || !data) return;
      // Normalise the JobSchema shape onto the keys the header reads (same keys the rail uses).
      setFetchedJob({
        "Job Id": jobId,
        "Job Title": data.job_title,
        "Job Code": `JOB-${String(jobId).replace(/-/g, "").slice(0, 6).toUpperCase()}`,
        Location: data.location,
        "Posting Date": data.date_posted ?? null,
        weighting_parameters: data.weighting_parameters,
        dual_model_scoring: data.dual_model_scoring,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [jobId, inRail]);

  const alert = useCallback(
    (alertType, alertMessage) =>
      dispatch(showAppAlert({ show: true, alertType, alertMessage })),
    [dispatch]
  );

  // --- data ---------------------------------------------------------------
  // Shortlist now rides on the ranking row ("Shortlisted"), so there's no separate fetch —
  // refreshing the rankings refreshes the shortlist.
  const refresh = useCallback(
    async ({ force = false, quiet = false } = {}) => {
      if (!jobId) return;
      // `quiet` is for the pipeline poll. `loading` swaps the whole table for skeletons, which is
      // right for a first load and completely wrong every three seconds — the rows the user is
      // watching would vanish and come back on a timer. A quiet refresh replaces the data
      // underneath the table without dismantling it.
      if (!quiet) setLoading(true);
      try {
        await loadRankings(jobId, { force });
      } catch (e) {
        // Nor does a failed poll deserve a toast: the work is server-side and continues
        // regardless, and the next tick is three seconds away. Only a refresh the user actually
        // asked for gets to complain out loud.
        if (!quiet) alert("error", e.message || "Failed to load candidates.");
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [jobId, loadRankings, alert]
  );

  useEffect(() => {
    refresh();
    // Selection is per-job; the rail's selectJob already clears it, but a direct
    // URL hit doesn't go through the rail.
    dispatch(clearCandidates());
    setDetailId(null);
    setScreeningByResumeId({});
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = useMemo(() => rankings.map(toRow), [rankings]);

  // Keep refetching while any resume is still moving through the pipeline. This is what makes a
  // mid-upload refresh survivable: the state lives in the database, so reloading the page picks
  // the rows back up exactly where they were, and this poll carries them to the finish.
  //
  // The predicate excludes stale rows deliberately (see isInProgress). A row nothing is working
  // on will never change on its own, and polling for it would mean hammering this endpoint every
  // three seconds forever — which is precisely what the migration's backfilled orphans would
  // cause, since they sit at a non-terminal 'parsed' with no worker behind them.
  const workInFlight = useMemo(() => hasWorkInFlight(allRows), [allRows]);

  useEffect(() => {
    if (!workInFlight || !jobId) return undefined;
    const id = setInterval(() => refresh({ force: true, quiet: true }), PIPELINE_POLL_MS);
    return () => clearInterval(id);
  }, [workInFlight, jobId, refresh]);

  // Counted from the rows on screen, not from jobStats, and from the same predicates the tabs
  // filter on — deriving the badge from anything else is how a tab comes to read "12" and show 11.
  // The five decision tabs sum to `all`; countByStage owns that invariant, and a test defends it.
  const counts = useMemo(() => countByStage(allRows), [allRows]);

  const visibleRows = useMemo(
    () =>
      sortRows(
        filterByStage(allRows, stage).filter((r) => matchesQuery(r, query)),
        sort.key,
        sort.dir
      ),
    [allRows, stage, sort, query]
  );

  // --- screening columns ----------------------------------------------------
  // Declared AFTER visibleRows on purpose: the useMemo below reads it, and a useMemo factory
  // runs synchronously during render, so hoisting this block above visibleRows is a TDZ
  // ReferenceError on first render — not a style nit.
  const { columns: screeningColumns, toggle: toggleScreeningColumn } = useScreeningColumns();

  // What the picker can offer: every screening_data key this job actually captured.
  const screeningFieldOptions = useMemo(
    () => availableFields(screeningByResumeId),
    [screeningByResumeId]
  );

  // What the table actually renders: the selected columns, minus any that are empty for every
  // candidate on this job. The selection is global but screening questions vary by role, so
  // columns picked while looking at a Sales job would otherwise render as a wall of dashes on an
  // Engineering job. Degrade to fewer columns, never to dead ones.
  //
  // Ordered by screeningFieldOptions (registry order), NOT by screeningColumns — that array is in
  // the order the user clicked the checkboxes, which is not a display order.
  const visibleScreeningColumns = useMemo(() => {
    if (stage !== "screening") return [];
    return screeningFieldOptions.filter(
      (field) =>
        screeningColumns.includes(field.key) &&
        visibleRows.some((row) => {
          const data = getScreeningData(screeningByResumeId?.[row.resumeId]?.results);
          return formatValue(data?.[field.key]) !== "—";
        })
    );
  }, [stage, screeningFieldOptions, screeningColumns, visibleRows, screeningByResumeId]);

  // --- created/updated columns ----------------------------------------------
  // Unlike the screening columns these are NOT filtered against the data: a column of em-dashes is
  // the correct answer for resumes uploaded before the timestamps existed. Hiding it because it's
  // empty would mean the user toggles "Created" on and nothing happens.
  const { columns: metaColumns, toggle: toggleMetaColumn } = useMetaColumns();

  const visibleMetaColumns = useMemo(
    () => META_FIELDS.filter((field) => metaColumns.includes(field.key)),
    [metaColumns]
  );

  // One picker, two registries behind it. The `meta:` prefix is what makes routing a toggle back to
  // the right hook safe — see metaColumns.js.
  const pickerFields = useMemo(
    () => [...META_FIELDS, ...(stage === "screening" ? screeningFieldOptions : [])],
    [stage, screeningFieldOptions]
  );

  const pickerSelected = useMemo(
    () => [...metaColumns, ...screeningColumns],
    [metaColumns, screeningColumns]
  );

  const togglePickerColumn = useCallback(
    (key) => (isMetaKey(key) ? toggleMetaColumn(key) : toggleScreeningColumn(key)),
    [toggleMetaColumn, toggleScreeningColumn]
  );

  // --- screening status -----------------------------------------------------
  // One request for the whole job. This used to be a fan-out of one request per candidate,
  // which forced the SCREEN column to load lazily on the Screening tab only; now it can
  // populate on every tab, and the detail panel no longer needs an on-demand fetch.
  //
  // Candidates with no screening call are absent from the map — missing means "not
  // scheduled", not "failed to load".
  // Paused first, for the same reason screenProgress() checks it first: a paused call's lead_status
  // is frozen at whatever it was, so reading it would export "Pending" for a call we have
  // definitively stopped — and disagree with the "Paused" chip the table is showing.
  const label = (status) =>
    status.paused
      ? "Paused"
      : SCREEN_LABEL[status.lead_status] || (status.status ? "Scheduled" : "—");

  // Ref, so the fallback below can see who's been screened without making this callback
  // depend on allRows (which would re-run the poll effect on every render).
  const scheduledIdsRef = useRef([]);
  scheduledIdsRef.current = allRows
    .filter((r) => r.screeningScheduled)
    .map((r) => r.resumeId);

  const refreshScreening = useCallback(async () => {
    if (!jobId) return;

    let map = {};
    try {
      map = await getScreeningStatusesAPI(jobId);
    } catch (e) {
      // Don't swallow this. An empty map is indistinguishable from "nobody has been
      // screened", so a failing bulk call used to render as a confident, wrong
      // "no screening call has been scheduled".
      console.error("Bulk screening status failed:", e);
    }

    const scheduled = scheduledIdsRef.current;

    // Rankings say these candidates HAVE been screened, but the bulk call gave us nothing
    // for them. That means the bulk endpoint failed, isn't deployed, or returned a shape we
    // didn't understand — so fall back to the per-candidate endpoint, which is what the old
    // screening dashboard uses and is known to work.
    const missing = scheduled.filter((id) => !map[id]);
    if (missing.length) {
      console.warn(
        `Bulk screening status returned nothing for ${missing.length} screened candidate(s); falling back to per-candidate lookups.`
      );
      const entries = await Promise.all(
        missing.map(async (id) => {
          try {
            return [id, await getScreeningStatusAPI(jobId, id)];
          } catch (_) {
            return [id, null];
          }
        })
      );
      entries.forEach(([id, status]) => {
        if (status?.status) map = { ...map, [id]: status };
      });
    }

    setScreeningByResumeId(
      Object.fromEntries(
        Object.entries(map).map(([id, status]) => [id, { ...status, label: label(status) }])
      )
    );
  }, [jobId]);

  // Read the latest statuses from a ref rather than a setState updater — updaters must
  // stay pure (StrictMode double-invokes them), and firing a fetch inside one would
  // double-request every poll.
  const screeningRef = useRef({});
  screeningRef.current = screeningByResumeId;

  // Stable primitive: changes when the set of screened candidates does. Rankings load
  // asynchronously, so on the first pass we don't yet know who's been screened — without
  // this the fallback above would never fire, because it'd run against an empty list.
  const scheduledKey = scheduledIdsRef.current.join(",");

  useEffect(() => {
    if (!jobId) return;
    refreshScreening();

    const tick = setInterval(() => {
      // Keep polling only while something is still in flight; stop once every result has
      // settled. (A job with nothing scheduled has an empty map, so this never polls.)
      const statuses = Object.values(screeningRef.current);
      if (statuses.length && statuses.some(isPending)) refreshScreening();
    }, SCREENING_POLL_MS);

    return () => clearInterval(tick);
  }, [jobId, scheduledKey, refreshScreening]);

  // --- interview results (lazy: only on the Interview tab) ------------------
  // fetchInterviewData inner-joins resume + interview scores, so it 404s until at least
  // one candidate has actually been interviewed. That's expected, not an error — treat
  // it as "no results yet" rather than surfacing a failure.
  useEffect(() => {
    if (stage !== "interview" || !jobId) return;
    let cancelled = false;
    fetchInterviewData(jobId)
      .then((rows) => {
        if (cancelled) return;
        setInterviewByResumeId(
          Object.fromEntries((rows || []).map((r) => [r["Resume Id"], r]))
        );
      })
      .catch(() => {
        if (!cancelled) setInterviewByResumeId({});
      });
    return () => {
      cancelled = true;
    };
  }, [stage, jobId, rankings]);

  // --- batch call status ---------------------------------------------------
  useEffect(() => {
    if (!jobId) return;
    const check = async () => {
      try {
        setBatchStatus(await getBatchStatusAPI(jobId));
      } catch (_) {
        /* non-fatal */
      }
    };
    check();
    const tick = setInterval(check, BATCH_POLL_MS);
    return () => clearInterval(tick);
  }, [jobId]);

  // --- selection -----------------------------------------------------------
  const selectedIds = useMemo(
    () => new Set(selectedCandidates.map((c) => c.ResumeId)),
    [selectedCandidates]
  );

  const toggle = (row) => {
    if (selectedIds.has(row.resumeId)) {
      dispatch(removeCandidate({ ResumeId: row.resumeId }));
    } else {
      dispatch(addCandidate({ ResumeId: row.resumeId, name: row.name, rank: row.rank }));
    }
  };

  /**
   * Select-all acts on what you can SEE — the current tab, filtered by the current search — not on
   * every candidate in the job. Selecting rows a search has hidden is how someone schedules calls
   * for people they never looked at. Deselect removes exactly the same set, so it undoes itself and
   * leaves any selection made on another tab alone.
   */
  const toggleAll = (rowsOnScreen, select) => {
    rowsOnScreen.forEach((r) =>
      dispatch(
        select
          ? addCandidate({ ResumeId: r.resumeId, name: r.name, rank: r.rank })
          : removeCandidate({ ResumeId: r.resumeId })
      )
    );
  };

  // Ported from Report.js: Top N picks from candidates not already interview-selected.
  const selectableRows = useMemo(
    () => sortRows(allRows.filter((r) => !r.interviewSelected), "total", "desc"),
    [allRows]
  );

  const selectTop = (n) => {
    dispatch(clearCandidates());
    selectableRows.slice(0, n).forEach((r) =>
      dispatch(addCandidate({ ResumeId: r.resumeId, name: r.name, rank: r.rank }))
    );
  };

  const clear = useCallback(() => dispatch(clearCandidates()), [dispatch]);

  /**
   * What F5 would do, without losing where you were.
   *
   * `force` bypasses the rankings cache in ConsoleContext — without it this button would re-serve
   * the same stale rows it was pressed to escape. The interview map refetches on its own: its
   * effect keys off `rankings`, which force-reloading replaces. Tab, sort, search, selection and
   * scroll all survive, which is the whole reason this exists instead of window.location.reload().
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([refresh({ force: true }), refreshScreening()]);
      refreshCounts();
    } finally {
      setRefreshing(false);
    }
  }, [refresh, refreshScreening, refreshCounts]);

  /**
   * Re-run the pipeline for a resume that failed or stalled.
   *
   * Reprocesses from the PDF still sitting in S3 — the user does not re-upload anything. The
   * refresh afterwards puts the row back into a non-terminal status, which restarts the poll on
   * its own; there is no separate "now watch it" step.
   */
  const handleRetry = useCallback(
    async (resumeId) => {
      try {
        await retryResumeAPI(jobId, resumeId);
        alert("success", "Reprocessing started.");
        await refresh({ force: true, quiet: true });
      } catch (e) {
        alert("error", e.message || "Could not retry this resume.");
      }
    },
    [jobId, alert, refresh]
  );

  // Keyboard: esc clears selection, S schedules screening, I schedules interviews.
  // None of them fire while the user is typing in a field.
  const keysRef = useRef({});
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const k = keysRef.current;
      if (e.key === "Escape") k.clear?.();
      else if (e.key.toLowerCase() === "s") k.screening?.();
      else if (e.key.toLowerCase() === "i") k.interview?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // --- guards --------------------------------------------------------------
  // Computed against ALL rows, never the stage-filtered view: selection persists
  // across tabs, so a candidate picked on All must still resolve while Screening
  // is open — otherwise the guard finds nothing and the button wrongly enables.
  // The selected candidates as full rows. Redux stores only {ResumeId, name, rank}, but every
  // guard needs the decision and the screening flags, which live on the row.
  const pickedRows = useMemo(() => {
    const byId = new Map(allRows.map((r) => [r.resumeId, r]));
    return selectedCandidates.map((sc) => byId.get(sc.ResumeId)).filter(Boolean);
  }, [allRows, selectedCandidates]);

  const guards = useMemo(
    () => ({
      missingPhone: pickedRows.filter((r) => !r.phone),
      alreadyScreened: pickedRows.filter((r) => r.screeningScheduled),
      alreadyInterviewed: pickedRows.filter((r) => r.interviewSelected),
      // Depends on screeningByResumeId, which the poll refreshes — so the Shortlist button
      // enables itself the moment a call lands, without the user re-selecting anything.
      ...shortlistGuards(pickedRows, screeningByResumeId),
      ...pauseGuards(pickedRows, screeningByResumeId),
    }),
    [pickedRows, screeningByResumeId]
  );

  // --- actions -------------------------------------------------------------
  // None of these navigate away. Report.js pushed you to /dashboard/screening/:jobId
  // and /dashboard/interviews/:jobId after scheduling, which would eject the user
  // from the console. Here we refresh and switch the stage tab instead.
  const goToStage = (next) => setSearchParams({ stage: next });

  // One handler for all four decisions — they are one field, so they are one write. Rejecting or
  // backlogging also pauses any live call, which the server does for us (it owns the vendor call).
  const handleDecide = async (decision) => {
    // Re-check here, not just on the button — a disabled button is a hint, not an invariant, and
    // the same rule is enforced server-side. Only shortlist is guarded; reject/backlog/undo need
    // no evidence, which is why nothing gates them here either.
    if (decision === DECISION.SHORTLISTED) {
      const reason = shortlistReason({ stage, ...guards });
      if (reason) {
        alert("error", reason);
        return;
      }
    }

    const ids = selectedCandidates.map((c) => c.ResumeId);
    setDecisionLoading(true);
    try {
      const result = await setDecisionAPI(jobId, ids, decision);
      const updated = result?.updated?.length ?? ids.length;
      // `ignored` don't belong to this job; `refused` failed the server's own shortlist rule;
      // `pause_failures` were decided but their call could not be stopped — that last one is the
      // one that costs money and reaches a candidate, so it gets said out loud.
      const ignored = result?.ignored?.length || 0;
      const refused = result?.refused?.length || 0;
      const pauseFailures = result?.pause_failures?.length || 0;

      clear();
      await refresh({ force: true });
      await refreshCounts();

      const problems = [
        refused && `${refused} refused (not screened yet)`,
        ignored && `${ignored} ignored (not part of this job)`,
        pauseFailures && `${pauseFailures} could not be paused — they may still be called`,
      ].filter(Boolean);

      alert(
        problems.length ? "warning" : "success",
        problems.length
          ? `${updated} moved to ${DECISION_LABEL[decision]} — ${problems.join(", ")}.`
          : `${updated} candidate${updated === 1 ? "" : "s"} moved to ${DECISION_LABEL[decision]}.`
      );

      // Follow the candidates. Undo has no tab of its own — they fall back to Review or Screening
      // depending on whether they were ever called, so there is no single place to send the user.
      if (decision !== DECISION.UNDECIDED) goToStage(DECISION_TAB[decision]);
    } catch (e) {
      alert("error", e.message || "Failed to update the decision.");
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleScreening = async () => {
    // Re-check phone here, not just on the button. A disabled button is a hint, not an
    // invariant — the keyboard shortcut, the ⌘K action and a stale render can all reach
    // this, and scheduling a call for someone with no number just fails downstream.
    const noPhone = selectedCandidates
      .map((sc) => allRows.find((r) => r.resumeId === sc.ResumeId))
      .filter((r) => r && !r.phone);

    if (noPhone.length) {
      alert(
        "error",
        `Can't schedule screening — ${noPhone
          .map((r) => r.name)
          .join(", ")} ${noPhone.length === 1 ? "has" : "have"} no phone number.`
      );
      return;
    }

    setScreeningLoading(true);
    try {
      const outcomes = await Promise.allSettled(
        selectedCandidates.map((c) => scheduleScreeningCallAPI(jobId, c.ResumeId))
      );
      // allSettled never rejects, so a partial failure used to look like a clean success.
      // Count them and say so — silently dropping half a batch is the worst outcome here.
      const scheduled = outcomes.filter((o) => o.status === "fulfilled").length;
      const failed = outcomes.length - scheduled;

      if (!scheduled) {
        alert("error", "No screening calls could be scheduled.");
      } else if (failed) {
        alert(
          "warning",
          `${scheduled} screening call${scheduled === 1 ? "" : "s"} scheduled, ${failed} failed.`
        );
      } else {
        alert(
          "success",
          `${scheduled} screening call${scheduled === 1 ? "" : "s"} scheduled.`
        );
      }

      clear();
      await refresh({ force: true });
      await refreshScreening();
      await refreshCounts();
      goToStage("screening");
    } catch (e) {
      alert("error", e.message || "Failed to schedule screening calls.");
    } finally {
      setScreeningLoading(false);
    }
  };

  // Pause / resume the scheduler's outbound calls. `pause` is explicit rather than derived from
  // the current state here: SelectionBar already decided which of the two the button meant, and
  // re-deriving it from a status map that may have polled underneath us could invert the action.
  const handlePauseResume = async (pause) => {
    const reason = pauseReason({ stage, ...guards });
    if (reason) {
      alert("error", reason);
      return;
    }

    const targets = pause ? guards.pausable : guards.resumable;
    setPauseLoading(true);
    try {
      const outcomes = await Promise.allSettled(
        targets.map((r) => pauseScreeningCallAPI(jobId, r.resumeId, pause))
      );
      // allSettled never rejects, so a partial failure would otherwise read as a clean success —
      // and here that means telling a recruiter the calls stopped while the phone is still ringing.
      const done = outcomes.filter((o) => o.status === "fulfilled").length;
      const failed = outcomes.length - done;
      const verb = pause ? "paused" : "resumed";

      if (!done) {
        alert("error", `No screening calls could be ${verb}.`);
      } else if (failed) {
        alert(
          "warning",
          `${done} screening call${done === 1 ? "" : "s"} ${verb}, ${failed} failed — ` +
            `${pause ? "those candidates may still be called." : "those calls are still stopped."}`
        );
      } else {
        alert("success", `${done} screening call${done === 1 ? "" : "s"} ${verb}.`);
      }

      clear();
      await refreshScreening();
    } catch (e) {
      alert("error", e.message || `Failed to ${pause ? "pause" : "resume"} screening calls.`);
    } finally {
      setPauseLoading(false);
    }
  };

  const handleInterview = async () => {
    try {
      await selectCandidatesForInterviewAPI(jobId, selectedCandidates);
      alert("success", "Candidates selected for interviews.");
      clear();
      await refresh({ force: true });
      await refreshCounts();
      goToStage("interview");
    } catch (e) {
      alert("error", e.message || "Failed to select candidates.");
    }
  };

  const handleStartCalls = async ({ windowStart, windowEnd, timezone }) => {
    setBatchLoading(true);
    try {
      const resumeIds = selectedCandidates.map((c) => c.ResumeId);
      const result = await startBatchCallAPI(
        jobId,
        resumeIds,
        windowStart,
        windowEnd,
        timezone
      );
      setBatchStatus({ active: true, current: 1, total: result.total, status: "running" });
      setBatchOpen(false);
      alert("success", result.message || "AI calls started.");
    } catch (e) {
      alert("error", e.message || "Failed to start AI calls.");
    } finally {
      setBatchLoading(false);
    }
  };

  // Row click opens the detail panel in-console. The full resume (which lives on a
  // separate public route) is still one click away from inside the panel.
  const openCandidate = (row) => setDetailId(row.resumeId);

  const detailRow = allRows.find((r) => r.resumeId === detailId) || null;

  // Moving a single candidate to screening from the detail panel. Same API as the bulk
  // action, just scoped to one — including the phone guard: the button is disabled
  // without a number, but a disabled button is a hint, not an invariant.
  const moveToScreening = async (row) => {
    if (!row.phone) {
      alert("error", `Can't schedule screening — ${row.name} has no phone number.`);
      return;
    }

    try {
      await scheduleScreeningCallAPI(jobId, row.resumeId);
      alert("success", `${row.name} moved to screening.`);
      setDetailId(null);
      await refresh({ force: true });
      await refreshScreening();
      goToStage("screening");
    } catch (e) {
      alert("error", e.message || "Failed to move candidate to screening.");
    }
  };

  // The S/I shortcuts must obey exactly the guards the buttons do — a keystroke must
  // never do something the equivalent disabled button refuses to.
  const canScreen =
    selectedCandidates.length > 0 &&
    !screeningLoading &&
    !guards.missingPhone.length &&
    !guards.alreadyScreened.length;
  // INTERVIEWS_ENABLED first: this one flag gates the I shortcut and the ⌘K action as
  // well as the buttons, so a keystroke can't reach what the UI no longer offers.
  const canInterview =
    INTERVIEWS_ENABLED &&
    selectedCandidates.length > 0 &&
    !guards.alreadyInterviewed.length;

  keysRef.current = {
    clear,
    screening: canScreen ? handleScreening : undefined,
    interview: canInterview ? handleInterview : undefined,
  };

  // Feed the ⌘K palette (rendered by ConsoleLayout) with this job's candidates + actions.
  useEffect(() => {
    setCommands({
      candidates: allRows.map((r) => ({
        name: r.name,
        stage: r.stage,
        open: () => setDetailId(r.resumeId),
      })),
      actions: [
        { label: "Upload resumes", run: () => setUploadOpen(true) },
        { label: "Job settings", run: () => setSettingsOpen(true) },
        ...(canScreen
          ? [{ label: "Schedule screening calls", kbd: "S", run: handleScreening }]
          : []),
        ...(canInterview
          ? [{ label: "Schedule interviews", kbd: "I", run: handleInterview }]
          : []),
      ],
    });
    // handleScreening/handleInterview are stable enough for this registration; the
    // guard flags are what actually gate them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, canScreen, canInterview, setCommands]);

  // Exports what you're actually looking at — the active stage tab, in the current sort
  // order — rather than always dumping the full list. Screening columns are populated on
  // every tab now that the status arrives in one bulk request.
  const handleExport = () => {
    const rows = visibleRows.map((r) => {
      const screening = screeningByResumeId[r.resumeId];
      const best = getBestScreeningResult(screening?.results);
      return [
        r.rank,
        r.name,
        r.email,
        r.phone,
        // The two headline numbers sit together. Screening score used to trail the row, so the
        // comparison a recruiter actually makes — résumé said 8, the call said 4 — was eleven
        // columns apart. Same score2 the per-candidate export uses, so the two files agree.
        score2(r.total),
        score2(best?.interview_rating),
        score2(r.skills),
        score2(r.exp),
        score2(r.edu),
        score2(r.p2p),
        r.stage,
        // The decision, not a yes/no shortlist flag — exporting a boolean would report a rejected
        // candidate and an undecided one identically, as "no".
        r.decision,
        screening?.label || "",
      ];
    });

    exportToCsv(
      `candidates-${jobId}${stage === "all" ? "" : `-${stage}`}.csv`,
      [
        "Rank",
        "Candidate",
        "Email",
        "Phone",
        "Total score",
        "Screen score",
        "Skills",
        "Experience",
        "Education",
        "P2P",
        "Stage",
        "Decision",
        "Screening status",
      ],
      rows
    );
  };

  const outlineBtn = {
    border: `1px solid ${color.borderInput}`,
    borderRadius: `${radius.button}px`,
    font: `500 12px ${font.sans}`,
    color: color.text,
    px: 1.5,
    py: 0.75,
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.25,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ font: `600 16px ${font.sans}`, color: color.text }}>
            {job?.["Job Title"] || "Job"}
          </Box>
          {/* Job Code is derived from the job's UUID — stable and unique, but NOT
              sequential. The mock's "JOB-0142" implied a counter that doesn't exist. */}
          <Box sx={{ mt: 0.25, font: `400 12px ${font.mono}`, color: color.textMuted }}>
            {[
              job?.["Job Code"],
              job?.Location,
              job?.["Posting Date"] ? `posted ${formatPostingDate(job["Posting Date"])}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Box>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={14} stroke={1.75} color={color.textMuted} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                font: `400 12.5px ${font.sans}`,
                bgcolor: color.surface,
                borderRadius: `${radius.button}px`,
              },
              "& .MuiOutlinedInput-input": { py: 0.875 },
            }}
          />
          <Button
            sx={outlineBtn}
            startIcon={<IconRefresh size={15} stroke={1.75} />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
          <Button
            sx={outlineBtn}
            startIcon={<IconDownload size={15} stroke={1.75} />}
            onClick={handleExport}
            disabled={!visibleRows.length}
          >
            Export
          </Button>
          <Button
            sx={outlineBtn}
            startIcon={<IconUpload size={15} stroke={1.75} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload resumes
          </Button>
          <Button
            sx={outlineBtn}
            startIcon={<IconSettings size={15} stroke={1.75} />}
            onClick={() => setSettingsOpen(true)}
          >
            Job settings
          </Button>
        </Box>
      </Box>

      <StageTabs
        active={stage}
        counts={counts}
        onChange={goToStage}
        onSelectTop={selectTop}
        selectableCount={selectableRows.length}
        // The Interview tab is where you'd actually go to open a slot window and invite
        // people, so the action lives with the tab rather than in the global header.
        action={
          // The picker is no longer the Screening tab's action — it sits BESIDE each tab's action,
          // because Created/Updated are columns on every tab. Screening's data columns just join
          // the same menu when that tab is open.
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {stage === "interview" ? (
              <Button sx={outlineBtn} onClick={() => setInterviewOpen(true)}>
                Schedule interviews
              </Button>
            ) : stage === "all" ? (
              <Button
                sx={outlineBtn}
                onClick={() => setPeerOpen(true)}
                disabled={allRows.length < 2}
              >
                Peer compare
              </Button>
            ) : null}
            <ColumnPicker
              fields={pickerFields}
              selected={pickerSelected}
              onToggle={togglePickerColumn}
            />
          </Box>
        }
      />

      <CandidateTable
        rows={visibleRows}
        loading={loading}
        sort={sort}
        onSort={(key) =>
          setSort((s) =>
            s.key === key
              ? { key, dir: s.dir === "desc" ? "asc" : "desc" }
              : { key, dir: "desc" }
          )
        }
        selectedIds={selectedIds}
        onToggle={toggle}
        onToggleAll={toggleAll}
        onOpenCandidate={openCandidate}
        screeningByResumeId={screeningByResumeId}
        screeningView={stage === "screening"}
        screeningColumns={visibleScreeningColumns}
        metaColumns={visibleMetaColumns}
        interviewByResumeId={interviewByResumeId}
        interviewView={stage === "interview"}
        onUpload={() => setUploadOpen(true)}
        onRetry={handleRetry}
      />

      <SelectionBar
        selectedCount={selectedCandidates.length}
        stage={stage}
        guards={guards}
        picked={pickedRows}
        batchActive={Boolean(batchStatus?.active)}
        screeningLoading={screeningLoading}
        pauseLoading={pauseLoading}
        decisionLoading={decisionLoading}
        onClear={clear}
        onDecide={handleDecide}
        onPauseResume={handlePauseResume}
        onScreening={handleScreening}
        onInterview={handleInterview}
        onStartCalls={() => setBatchOpen(true)}
      />

      <BatchCallDialog
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        count={selectedCandidates.length}
        missingPhoneCount={guards.missingPhone.length}
        loading={batchLoading}
        onConfirm={handleStartCalls}
      />

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        jobId={jobId}
        jobTitle={job?.["Job Title"]}
        onComplete={() => refresh({ force: true })}
      />

      <JobPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        job={job}
        onSaved={reloadJobs}
        onDeleted={async () => {
          // The job we're looking at no longer exists — reload the rail and bounce to
          // the index, which lands on whatever job is now first.
          await reloadJobs();
          navigate(paths.v1.jobs, { replace: true });
        }}
      />

      <PeerCompareDialog
        open={peerOpen}
        onClose={() => setPeerOpen(false)}
        jobId={jobId}
        candidateCount={allRows.length}
        onDone={() => refresh({ force: true })}
      />

      <InterviewPanel
        open={interviewOpen}
        onClose={() => setInterviewOpen(false)}
        jobId={jobId}
        candidates={allRows.filter((r) => r.interviewSelected)}
        onDone={() => refresh({ force: true })}
      />

      <CandidateDetailPanel
        open={Boolean(detailRow)}
        onClose={() => setDetailId(null)}
        row={detailRow}
        jobId={jobId}
        screening={detailId ? screeningByResumeId[detailId] : null}
        interview={detailId ? interviewByResumeId[detailId] : null}
        onMoveToScreening={moveToScreening}
        onSaveEmail={async (resumeId, email) => {
          await updateCandidateEmailAPI(jobId, resumeId, email);
          await refresh({ force: true });
          alert("success", "Email updated.");
        }}
        onSavePhone={async (resumeId, phone) => {
          await updateCandidatePhoneAPI(jobId, resumeId, phone);
          await refresh({ force: true });
          alert("success", "Phone updated.");
        }}
      />
    </>
  );
};

export default Console;
